import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import {
  DOCUMENT_CLASSIFICATION_FILTER_ALL,
  type DocumentClassificationFilterId,
  type DocumentClassificationFilterOption,
} from "@/components/case-checking/documentClassificationFilters";
import { Button } from "@/components/ui/button/Button";

/** Approximate chip width when layout measurement is unavailable (e.g. jsdom). */
const CHIP_UNIT_PX = 76;
const CHIP_GAP_PX = 6;
const MIN_CHIPS_PER_PAGE = 3;
const DEFAULT_CHIPS_PER_PAGE = 8;

function chunkOptions(
  options: DocumentClassificationFilterOption[],
  chipsPerPage: number,
): DocumentClassificationFilterOption[][] {
  const perPage = Math.max(1, chipsPerPage);
  const pages: DocumentClassificationFilterOption[][] = [];
  for (let i = 0; i < options.length; i += perPage) {
    pages.push(options.slice(i, i + perPage));
  }
  return pages.length > 0 ? pages : [options];
}

function packOptionsByWidths(
  options: DocumentClassificationFilterOption[],
  chipWidths: number[],
  viewportWidth: number,
): DocumentClassificationFilterOption[][] {
  const measured = chipWidths.some((w) => w > 0);
  if (!measured || viewportWidth <= 0) {
    const fit = Math.floor(viewportWidth / CHIP_UNIT_PX);
    const chipsPerPage = Math.max(
      MIN_CHIPS_PER_PAGE,
      fit > 0 ? fit : DEFAULT_CHIPS_PER_PAGE,
    );
    return chunkOptions(options, chipsPerPage);
  }

  const pages: DocumentClassificationFilterOption[][] = [];
  let page: DocumentClassificationFilterOption[] = [];
  let usedWidth = 0;

  for (let i = 0; i < options.length; i++) {
    const chipWidth = chipWidths[i] ?? CHIP_UNIT_PX;
    const gap = page.length > 0 ? CHIP_GAP_PX : 0;
    const nextWidth = usedWidth + gap + chipWidth;

    if (page.length > 0 && nextWidth > viewportWidth) {
      pages.push(page);
      page = [options[i]];
      usedWidth = chipWidth;
    } else {
      page.push(options[i]);
      usedWidth = nextWidth;
    }
  }

  if (page.length > 0) {
    pages.push(page);
  }

  return pages.length > 0 ? pages : [options.slice(0, 1)];
}

function usePagedOptions(
  options: DocumentClassificationFilterOption[],
  viewportRef: RefObject<HTMLDivElement | null>,
  measureRef: RefObject<HTMLDivElement | null>,
  testIdPrefix: string,
): DocumentClassificationFilterOption[][] {
  const [pages, setPages] = useState(() => chunkOptions(options, DEFAULT_CHIPS_PER_PAGE));

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const measureRow = measureRef.current;
    if (!viewport || !measureRow) return;

    const recompute = () => {
      const viewportWidth = viewport.getBoundingClientRect().width;
      const chipElements = measureRow.querySelectorAll<HTMLElement>(
        `button[data-testid^="${testIdPrefix}-chip-"]`,
      );
      if (chipElements.length !== options.length) return;

      const chipWidths: number[] = [];
      for (let i = 0; i < options.length; i++) {
        chipWidths.push(chipElements[i]?.offsetWidth ?? 0);
      }
      setPages(packOptionsByWidths(options, chipWidths, viewportWidth));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(viewport);
    ro.observe(measureRow);
    return () => ro.disconnect();
  }, [measureRef, options, testIdPrefix, viewportRef]);

  return pages;
}

function ClassificationFilterChip({
  option,
  selected,
  onChange,
  testIdPrefix,
}: {
  option: DocumentClassificationFilterOption;
  selected: boolean;
  onChange: (id: DocumentClassificationFilterId) => void;
  testIdPrefix: string;
}) {
  return (
    <Button
      plain
      type="button"
      size="unstyled"
      aria-pressed={selected}
      onClick={() => onChange(option.id)}
      className={clsx(
        "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap",
        selected
          ? "bg-indigo-500/15 text-indigo-800 ring-1 ring-indigo-500/30 dark:text-indigo-200"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200/80 dark:bg-white/[0.06] dark:text-slate-300 dark:hover:bg-white/[0.1]",
      )}
      data-testid={`${testIdPrefix}-chip-${option.id === DOCUMENT_CLASSIFICATION_FILTER_ALL ? "all" : option.id}`}
    >
      {option.label}
    </Button>
  );
}

/** Paginated classification filter chips that fit within the available toolbar width. */
export function DocumentClassificationFilterChips({
  options,
  value,
  onChange,
  className,
  testIdPrefix = "document-classification-filter",
}: {
  options: DocumentClassificationFilterOption[];
  value: DocumentClassificationFilterId;
  onChange: (id: DocumentClassificationFilterId) => void;
  className?: string;
  testIdPrefix?: string;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const pages = usePagedOptions(options, viewportRef, measureRef, testIdPrefix);
  const [page, setPage] = useState(0);

  const activePageOptions = useMemo(() => pages[page] ?? [], [page, pages]);

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(0, pages.length - 1)));
  }, [pages.length]);

  useEffect(() => {
    const idx = pages.findIndex((pageOptions) => pageOptions.some((option) => option.id === value));
    if (idx >= 0) {
      setPage(idx);
    }
  }, [pages, value]);

  if (options.length <= 2) {
    return null;
  }

  const totalPages = pages.length;
  const canGoBack = page > 0;
  const canGoForward = page < totalPages - 1;

  return (
    <div
      className={clsx("flex w-full min-w-0 items-center gap-1", className)}
      role="group"
      aria-label="Filter by document type"
      data-testid={`${testIdPrefix}-chips`}
    >
      {canGoBack ? (
        <Button
          plain
          type="button"
          size="unstyled"
          className="shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/[0.08]"
          aria-label="Previous classification filters"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          data-testid={`${testIdPrefix}-page-prev`}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5" />
        </Button>
      ) : null}

      <div ref={viewportRef} className="relative min-w-0 flex-1 overflow-hidden" aria-live="polite">
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-1.5"
          aria-hidden
          data-testid={`${testIdPrefix}-measure-row`}
        >
          {options.map((option) => (
            <ClassificationFilterChip
              key={`measure-${option.id}`}
              option={option}
              selected={value === option.id}
              onChange={onChange}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
        <div
          className="flex items-center gap-1.5"
          data-testid={`${testIdPrefix}-page`}
          data-page={page}
        >
          {activePageOptions.map((option) => (
            <ClassificationFilterChip
              key={option.id}
              option={option}
              selected={value === option.id}
              onChange={onChange}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      </div>

      {canGoForward ? (
        <Button
          plain
          type="button"
          size="unstyled"
          className="shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/[0.08]"
          aria-label="Next classification filters"
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          data-testid={`${testIdPrefix}-page-next`}
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
}
